<template>
  <div class="ma-4 px-4">
    <searchBar
      @add="(dialog = true), (editMode = false)"
      @updateSearch="(v) => (search = v)"
      headerText=""
      @fileUploaded="initialise"
    />

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="programs"
            :custom-filter="customFilter"
            fixed-header
            class="secondary row-height-50"
          >
            <template #[`item.action`]="{ item }">
              <v-icon
                :disabled="!item.active"
                small
                class="mr-2"
                color="accent"
                @click="edit(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <template #[`item.active`]="{ item }">
              <v-chip
                small
                :color="item.active ? 'green' : 'red'"
                dark
                @click="openConfirm(item)"
              >
                {{ item.active ? "Active" : "Inactive" }}
              </v-chip>
            </template>
            <template #[`item.degree_specialization`]="{ item }">
              {{ getProgramName(item) }}
            </template>
            <template #[`item.duration`]="{ item }">
              {{ getDurationType(item) }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" width="800" persistent scrollable>
      <formDialog
        v-if="dialog"
        @closeDialog="dialog = false"
        @create="createProgram"
        @update="updateProgram"
        :degrees="degrees"
        :department_id="parseInt(department_id)"
        :departments="departments"
        :program="program"
        :edit="editMode"
      />
    </v-dialog>
    <ConfirmDialog
      v-if="showConfirmDialog"
      :show="showConfirmDialog"
      @delete="changeConfirmation"
      @close="showConfirmDialog = false"
      title="program"
      :name="`${currentItem.degree.short_name} ${
        currentItem.specialization ? ' - ' + currentItem.specialization : ''
      }`"
      :active="currentItem.active"
    />
  </div>
</template>

<script>
import formDialog from "@/components/program/form.vue";
import searchBar from "@/components/searchBar.vue";
import { durations } from "@/constants/enum";
import { programHeaders } from "@/constants/headers";
import ConfirmDialog from "@/components/confirm.vue";

export default {
  components: {
    formDialog,
    searchBar,
    ConfirmDialog,
  },
  data() {
    return {
      dialog: false,
      editMode: false,
      program: {},
      department_id: null,
      programs: [],
      search: "",
      headers: programHeaders,
      degrees: [],
      departments: [],
      durations: durations,
      showConfirmDialog: false,
      currentItem: {},
    };
  },
  watch: {},
  computed: {},
  created() {
    this.department_id = this.$route.params.id;
    this.headers = programHeaders.filter((h) => h.value !== "department.name");
    this.setBreadcrumbs();
    this.initialise();
  },
  methods: {
    setBreadcrumbs(departmentName) {
      const name = departmentName || this.$route.query.name;
      if (name) {
        this.$store.commit("setBreadcrumbs", [
          { text: "Departments", to: { name: "Departments" } },
          { text: name, to: { path: `/departments/${this.department_id}` } },
          { text: "Programmes" },
        ]);
      } else {
        this.$axios.get("departments").then((res) => {
          const department = res.data.data.find(
            (d) => d.id == this.department_id
          );
          this.setBreadcrumbs(department?.name);
        });
      }
    },
    async initialise() {
      this.getPrograms();
    },
    async getPrograms() {
      await this.$axios
        .get("programs")
        .then((res) => {
          const response = res.data.data;
          this.programs = response
            .filter((c) => c.department_id == this.department_id)
            .sort((a, b) => b.id - a.id);
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },
    async createProgram(data) {
      await this.$axios
        .post("programs", { program: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Programme created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateProgram(data) {
      await this.$axios
        .put("programs/" + data.id, { program: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Programme updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },

    edit(item) {
      this.program = { ...item };
      this.editMode = true;
      this.dialog = true;
    },
    resetValues() {
      this.editMode = false;
      this.dialog = false;
      this.program = {};
      this.showConfirmDialog = false;
      this.currentItem = {};
    },
    getDuration(item) {
      const durationObj = this.durations.find(
        (p) => p.id == item.duration_type
      );
      if (!durationObj) return ""; // Handle invalid duration_type

      const name = durationObj.name;
      return item.duration > 1 ? `${name}s` : name;
    },
    openConfirm(item, title) {
      this.currentItem = { ...item };
      this.showConfirmDialog = true;
    },
    changeConfirmation() {
      this.updateProgram({
        id: this.currentItem.id,
        active: !this.currentItem.active,
      });
    },
    getProgramName(item) {
      return (
        (item.degree?.short_name || "") +
        (item.specialization ? ` - ${item.specialization}` : "")
      );
    },
    getDurationType(item) {
      return item.duration + " " + this.getDuration(item);
    },
    showActiveStatus(item) {
      return item.active ? "Active" : "Inactive";
    },
    customFilter(value, search, item) {
      if (!search) return true;
      const degree = this.getProgramName(item);
      const duration = this.getDurationType(item);
      const activeStatus = this.showActiveStatus(item.active);
      return (
        JSON.stringify(item).toLowerCase().includes(search.toLowerCase()) ||
        degree.toLowerCase().includes(search.toLowerCase()) ||
        duration.toLowerCase().includes(search.toLowerCase()) ||
        activeStatus.toLowerCase().includes(search.toLowerCase())
      );
    },
  },
};
</script>

<style></style>
